import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import { CrudStyles } from '../shared/Crud';
import { DateTimeRow } from '../Deliveries/steps/DetailSectionShared';
import invoiceService from '../../services/invoiceService';
import MessageModal from '../shared/MessageModal';
import { formatDateIsoLocal } from '../../utils/deliveryDateRange';

const { Container, Header, Button, FormGroup } = CrudStyles;

const PAST_DAY_TO_CHARGE_MESSAGE = 'A data prevista de cobrança não pode ser anterior à data atual.';

export default function NewInvoice() {
    const navigate = useNavigate();
    const {
        selectedCustomerId,
        selectedCustomerName,
        isTransportCompany,
        isManagingOwnData,
    } = useCustomerSelector();
    const [dayToCharge, setDayToCharge] = useState('');
    const [dayToChargeError, setDayToChargeError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [messageModal, setMessageModal] = useState({ open: false, title: '', message: '' });

    const todayIso = formatDateIsoLocal(new Date());

    const hasValidCustomer = Boolean(
        selectedCustomerId && (!isTransportCompany || !isManagingOwnData),
    );

    useEffect(() => {
        if (!hasValidCustomer) {
            navigate(ROUTES.FATURAS, { replace: true });
        }
    }, [hasValidCustomer, navigate]);

    const handleDayToChargeChange = (e) => {
        const value = e.target.value;
        setDayToCharge(value);
        setDayToChargeError(value && value < todayIso ? PAST_DAY_TO_CHARGE_MESSAGE : null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!hasValidCustomer) return;
        if (dayToCharge && dayToCharge < todayIso) {
            setDayToChargeError(PAST_DAY_TO_CHARGE_MESSAGE);
            return;
        }

        setSubmitting(true);
        try {
            const invoice = await invoiceService.createInvoice({
                customerId: selectedCustomerId,
                dayToCharge: dayToCharge || undefined,
            });
            const invoiceId = invoice?.invoiceId ?? invoice?.id;
            if (invoiceId) {
                navigate(ROUTES.FATURAS_DETALHE(invoiceId));
            } else {
                setMessageModal({
                    open: true,
                    title: 'Erro',
                    message: 'Fatura criada, mas não foi possível obter o identificador.',
                });
            }
        } catch (err) {
            setMessageModal({
                open: true,
                title: 'Erro ao criar fatura',
                message: err?.message || 'Não foi possível criar a fatura.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (!hasValidCustomer) {
        return null;
    }

    return (
        <Container>
            <Header>
                <div>
                    <h1>Nova fatura</h1>
                    <p style={{ margin: '4px 0 0', fontSize: 14, color: '#64748b' }}>
                        Cliente: <strong>{selectedCustomerName || `#${selectedCustomerId}`}</strong>
                    </p>
                </div>
                <Button type="button" variant="outline" onClick={() => navigate(ROUTES.FATURAS)}>
                    Voltar
                </Button>
            </Header>

            <form onSubmit={handleSubmit}>
                <FormGroup>
                    <label htmlFor="invoice-day-to-charge">Data prevista de cobrança</label>
                    <DateTimeRow>
                        <FormGroup>
                            <label
                                htmlFor="invoice-day-to-charge"
                                style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}
                            >
                                Data
                            </label>
                            <input
                                id="invoice-day-to-charge"
                                type="date"
                                value={dayToCharge}
                                min={todayIso}
                                onChange={handleDayToChargeChange}
                                title={PAST_DAY_TO_CHARGE_MESSAGE}
                            />
                        </FormGroup>
                    </DateTimeRow>
                    {dayToChargeError ? (
                        <small style={{ color: '#b91c1c', fontSize: 12 }}>{dayToChargeError}</small>
                    ) : (
                        <small style={{ color: '#6b7280', fontSize: 12 }}>
                            Opcional — pode ser definida depois no rascunho.
                        </small>
                    )}
                </FormGroup>

                <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
                    <Button type="submit" variant="primary" disabled={submitting || Boolean(dayToChargeError)}>
                        {submitting ? 'Criando...' : 'Criar fatura'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => navigate(ROUTES.FATURAS)}>
                        Cancelar
                    </Button>
                </div>
            </form>

            {messageModal.open && (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
        </Container>
    );
}
